import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EmailService } from '@root/search/services/email.service';
import {
  cancelSharedReportEmail,
  sendSharedReportEmail,
  sharedReportEmailFailed,
  sharedReportEmailSucceeded
} from '@store/shared-report-email/shared-report-email.actions';
import { LOGOUT_SUCCESS } from '@store/user/user.actions';
import { defer, merge, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

const fallbackMessage = 'Unable to send the email. Please try again.';

function errorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    switch (error.status) {
      case 0:
        return 'Unable to connect. Check your connection and try again.';
      case 400:
        return 'The email could not be sent because the report link was changed. Restore the link and try again.';
      case 401:
        return 'Your session has expired. Sign in again to share this report.';
      case 403:
        return 'Sharing reports is not enabled for your account.';
      case 404:
        return 'This report link is no longer available. Generate a new link and try again.';
      case 410:
        return 'This report link has expired. Generate a new link and try again.';
      case 429:
        return 'Too many requests. Please wait before trying again.';
      case 503:
        return 'Sharing is temporarily unavailable. Please try again later.';
      default:
        return fallbackMessage;
    }
  }
  return fallbackMessage;
}

@Injectable()
export class SharedReportEmailEffects {
  send$ = createEffect(() => this.actions$.pipe(
    ofType(sendSharedReportEmail),
    switchMap(({ requestId, request }) =>
      defer(() => this.emails.send(request)).pipe(
        take(1),
        map(() => sharedReportEmailSucceeded({ requestId })),
        catchError((error: unknown) => of(sharedReportEmailFailed({ requestId, errorMessage: errorMessage(error) }))),
        takeUntil(merge(
          this.actions$.pipe(
            ofType(cancelSharedReportEmail),
            filter(action => action.requestId === requestId)
          ),
          this.actions$.pipe(ofType(LOGOUT_SUCCESS))
        ))
      )
    )
  ));

  constructor(private readonly actions$: Actions, private readonly emails: EmailService) {}
}
